import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnChanges, OnInit, SimpleChanges, ViewChild } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MenuItem } from 'primeng/api';
import { ButtonModule } from "primeng/button";
import { InputGroupModule } from "primeng/inputgroup";
import { InputGroupAddonModule } from "primeng/inputgroupaddon";
import { PaginatorModule, PaginatorState } from 'primeng/paginator';
import { Splitter, SplitterModule } from "primeng/splitter";
import { Textarea } from 'primeng/textarea';
import { Subscription } from "rxjs";
import { DatepickerComponent } from "../../../../../@core/components/ui/datepicker/datepicker.component";
import { DialogComponent } from '../../../../../@core/components/ui/dialog/dialog.component';
import { IAttachment } from "../../../../../@core/models/model";
import { SOCKET_EVENTS, SocketService } from "../../../../../@core/services/socket.service";
import { ToastService } from '../../../../../@core/services/toast.service';
import { DocumentPreviewComponent } from '../../../../../@shared/components/document-preview/document-preview.component';
import { FileDropComponent } from "../../../../../@shared/components/file-drop/file-drop.component";
import { FileDropTypes } from "../../../../../@shared/components/file-drop/file-drop.types";
import { GridListViewComponent } from "../../../../../@shared/components/grid-list-view/grid-list-view.component";
import { SearchBarComponent } from "../../../../../@shared/components/search-bar/search-bar.component";
import { DateFormatterPipe } from "../../../../../@shared/pipes/dateFormater.pipe";
import { EMBEDDINGS_GENERATION_STATUS } from "../../../../models/contract-management.model";
import { IConstructionAttachment } from "../../../../models/settings.model";
import { ConstructionAttachmentService } from "../../../../services/construction-attachment.service";
import { StatusBadgeComponent } from "../../knowledgebase/components/status-badge/status-badge.component";
import { TooltipModule } from 'primeng/tooltip';
import { UserNameDisplayComponent } from 'src/app/@shared/components/user-name-display/user-name-display.component';
import { ButtonComponent } from "../../../../../@core/components/ui/button/button.component";
import { DeleteConfirmationComponent } from "../../../../../@core/components/ui/delete-confirmation/delete-confirmation.component";
import { PopoverComponent } from "../../../../../@core/components/ui/popover/popover.component";
import { BadgeComponent } from "../../../../../@shared/components/badge/badge.component";

@Component({
    selector: 'app-innform-attachments',
    imports: [
        ButtonModule, InputGroupModule, InputGroupAddonModule, FormsModule, PaginatorModule, FileDropComponent, DateFormatterPipe,
        CommonModule, DocumentPreviewComponent, SplitterModule, Textarea, DatepickerComponent, DialogComponent,
        StatusBadgeComponent,
        GridListViewComponent,
        BadgeComponent,
        SearchBarComponent,
        TooltipModule,
        ButtonComponent,
        DeleteConfirmationComponent,
        PopoverComponent,
        UserNameDisplayComponent
    ],
    standalone: true,
    providers: [],
    templateUrl: './innform-attachments.component.html',
    styleUrl: './innform-attachments.component.scss'
})


export class InnformAttachmentsComponent implements OnInit, OnChanges {

    today: Date = new Date();
    searchValue: string = '';
    public allowedFileTypes: string[] = [FileDropTypes.PDF, FileDropTypes.DOCX, FileDropTypes.DOC];
    formFile: File | null = null;
    // Mock data for attachments
    attachments: IConstructionAttachment[] = [];
    totalRecords: number = 0;
    paginatedAttachments: any[] = [];
    first: number = 0;
    rows: number = 20;
    showPreviewDialog = false;
    selectedAttachment: IConstructionAttachment | null = null;
    currentUser: any; // Replace with your user type

    // Loading states
    attachmentsLoading: boolean = false;
    saveAttachmentLoading: boolean = false;

    panelSizes: number[] = [100, 0];
    minPanelSizes: number[] = [0, 0];

    @ViewChild('splitter') splitter!: Splitter;
    @ViewChild('fileDrop') fileDrop!: FileDropComponent;

    MENU_ITEM_OPTIONS = {
        EDIT: {
            label: 'Edit',
            icon: 'pi pi-pencil',
            command: () => this.editAttachment(this.selectedAttachment!)
        },
        DELETE: {
            label: 'Delete',
            icon: 'pi pi-trash',
            class: 'text-red-500',
            command: () => this.deleteAttachment(this.selectedAttachment!._id)
        }
    }

    menuItems: MenuItem[] = [];

    // Dialog properties
    showAttachmentDialog = false;
    isEditMode = false;
    attachmentForm: {
        name: string;
        description: string;
        file: File | null;
        attachment: IAttachment | null;
        expiryDate: Date | null;
    } = {
            name: '',
            description: '',
            expiryDate: null,
            file: null,
            attachment: null
        };

    private embeddingsStatusSubscription!: Subscription;
    EMBEDDINGS_GENERATION_STATUS = EMBEDDINGS_GENERATION_STATUS;

    deleteAttachmentDialogVisible = false;
    attachmentIdToDelete: string | null = null;

    onResizePanel(event: any) {
        this.panelSizes = event.sizes;
    }

    constructor(
        private constructionAttachmentService: ConstructionAttachmentService,
        private toast: ToastService,
        private cd: ChangeDetectorRef,
        private socketService: SocketService
    ) {

    }

    ngOnInit() {
        this.fetchAttachments();
        this.setupSocketListeners();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['attachments']) {
            this.paginatedAttachments = this.attachments.slice(this.first, this.first + this.rows);
        }
    }

    setupSocketListeners() {
        this.embeddingsStatusSubscription = this.socketService
            .on<{ documentKnowledge: string, status: EMBEDDINGS_GENERATION_STATUS, attachment?: IAttachment, constructionAttachment?: IConstructionAttachment }>(SOCKET_EVENTS.ATTACHMENT_EMBEDDINGS_GENERATION_STATUS_CHANGED)
            .subscribe(res => {
                if (res && res[0]) {
                    const { documentKnowledge, status, attachment, constructionAttachment } = res[0];
                    if (attachment) {
                        this.updateAttachmentStatus(attachment, status, constructionAttachment);
                    } else if (documentKnowledge) {
                        this.updateAttachmentStatusByDocumentKnowledge(documentKnowledge, status);
                    }
                    if (status === EMBEDDINGS_GENERATION_STATUS.FAILED) {
                        this.toast.error('The file appears to be corrupted. Please re-upload valid file');
                    }
                }
            });
    }

    updateAttachmentStatus(attachment: IAttachment, status: EMBEDDINGS_GENERATION_STATUS, constructionAttachment?: IConstructionAttachment) {
        this.attachments = this.attachments.map(item => item.attachment?._id === attachment._id ? { ...item, expiryDate: constructionAttachment?.expiryDate, documentKnowledge: { ...item.documentKnowledge, embeddingsStatus: status } } : item) as IConstructionAttachment[];
        this.paginatedAttachments = this.attachments.slice(this.first, this.first + this.rows);
    }

    updateAttachmentStatusByDocumentKnowledge(documentKnowledgeId: string, status: EMBEDDINGS_GENERATION_STATUS) {
        this.attachments = this.attachments.map(att => att.documentKnowledge?._id === documentKnowledgeId ? { ...att, documentKnowledge: { ...att.documentKnowledge, embeddingsStatus: status } } : att) as IConstructionAttachment[];
        this.paginatedAttachments = this.attachments.slice(this.first, this.first + this.rows);
    }


    fetchAttachments(event?: PaginatorState) {
        this.attachmentsLoading = true;
        const first = event?.first ?? this.first;
        const rows = event?.rows ?? this.rows;
        this.first = first;
        this.rows = rows;

        if (this.searchValue) {
            this.constructionAttachmentService.fetchAttachments({ skip: this.first, limit: this.rows, searchValue: this.searchValue }).subscribe(res => {
                if (res && res.success) {
                    this.totalRecords = res.data.total;
                    this.paginatedAttachments = res.data.constructionAttachments;
                    if (this.first === 0) {
                        this.attachments = res.data.constructionAttachments;
                    }
                }
                this.attachmentsLoading = false;
            });
            return;
        }

        if (this.first + this.rows <= this.attachments.length) {
            this.paginatedAttachments = this.attachments.slice(first, first + rows);
            this.attachmentsLoading = false;
            return;
        }
        this.constructionAttachmentService.fetchAttachments({ skip: this.first, limit: this.rows, searchValue: this.searchValue }).subscribe(res => {
            if (res && res.success) {
                this.totalRecords = res.data.total;
                if (first === this.attachments.length) {
                    this.attachments = [...this.attachments, ...res.data.constructionAttachments];
                    this.paginatedAttachments = this.attachments.slice(first, first + rows);
                } else {
                    this.paginatedAttachments = res.data.constructionAttachments;
                }
            }
            this.attachmentsLoading = false;
        })
    }

    onPageChange(event: PaginatorState) {
        this.fetchAttachments(event);
    }

    addAttachment() {
        this.isEditMode = false;
        this.attachmentForm = {
            name: '',
            description: '',
            expiryDate: null,
            file: null,
            attachment: null
        };
        this.selectedAttachment = null;
        this.showAttachmentDialog = true;
    }

    onSearch(value: string) {
        this.searchValue = value;
        this.first = 0;
        this.attachments = [];
        this.totalRecords = 0;
        this.paginatedAttachments = [];
        this.fetchAttachments();
    }

    /**
   * Handle form file upload event
   * @param event - File upload event containing files
   */
    onFormUpload(event: File | File[], attachmentId: string) {
        event = event as File[]
        this.formFile = event.length > 0 ? event[0] : null;
        if (this.formFile) {
            this.uploadAttachment(this.formFile, attachmentId);
        }

    }

    uploadAttachment(file: File, attachmentId: string) {
        this.constructionAttachmentService.uploadAttachment(file, attachmentId).subscribe(res => {
            if (res && res.success) {
                const newRecord = res.data;
                this.attachments = this.attachments.map(attachment => attachment._id === attachmentId ? { ...newRecord } : attachment);
                this.paginatedAttachments = this.attachments.slice(this.first, this.first + this.rows);
                this.toast.success('Attachment uploaded successfully');
                this.cd.detectChanges();
            }
        })
    }

    removeAttachment(attachmentId: string) {
        this.constructionAttachmentService.removeAttachment(attachmentId).subscribe(res => {
            if (res && res.success) {
                this.attachments = this.attachments.map(attachment => attachment._id === attachmentId ? { ...attachment, attachment: null, documentKnowledge: null } : attachment) as IConstructionAttachment[];
                this.paginatedAttachments = this.attachments.slice(this.first, this.first + this.rows);
                this.toast.success('Attachment removed successfully');
                this.cd.detectChanges();
            }
        })
    }

    deleteAttachment(attachmentId: string) {
        this.paginatedAttachments = this.attachments.slice(this.first, this.first + this.rows);
        this.attachmentIdToDelete = attachmentId;
        this.deleteAttachmentDialogVisible = true;
    }

    confirmDeleteAttachment() {
        if (this.attachmentIdToDelete) {
            this.constructionAttachmentService.deleteAttachment(this.attachmentIdToDelete).subscribe(res => {
                if (res && res.success) {
                    this.attachments = this.attachments.filter(attachment => attachment._id !== this.attachmentIdToDelete);
                    this.paginatedAttachments = this.attachments.slice(this.first, this.first + this.rows);
                    this.totalRecords = this.attachments.length;
                    this.toast.success('Attachment deleted successfully');
                    this.deleteAttachmentDialogVisible = false;
                    this.cd.detectChanges();
                } else {
                    this.deleteAttachmentDialogVisible = false;
                }
            });
        }
    }

    viewAttachment(attachment: IConstructionAttachment) {
        this.selectedAttachment = {} as IConstructionAttachment;
        setTimeout(() => {
            this.selectedAttachment = attachment;
            this.selectedAttachment.expiryDate = this.selectedAttachment.expiryDate ? new Date(this.selectedAttachment.expiryDate) : null;
            this.showPreviewDialog = true;
            // Smoothly transition panel sizes
            this.panelSizes = [50, 50];
            this.minPanelSizes = [30, 30];
            this.cd.detectChanges();
        }, 100);
    }

    closePreview() {
        // First, smoothly transition back to single panel
        this.panelSizes = [100, 0];
        this.minPanelSizes = [0, 0];
        this.cd.detectChanges();

        // Delay hiding the preview dialog to allow smooth transition
        setTimeout(() => {
            this.showPreviewDialog = false;
            this.selectedAttachment = {} as IConstructionAttachment;
            this.cd.detectChanges();
        }, 100);
    }

    getMenuItems(attachment: IConstructionAttachment): MenuItem[] {
        return [
            {
                ...this.MENU_ITEM_OPTIONS.EDIT,
                command: () => this.editAttachment(attachment)
            },
            {
                ...this.MENU_ITEM_OPTIONS.DELETE,
                command: () => this.deleteAttachment(attachment._id)
            }
        ];
    }

    handleMenuItemClick(attachment: IConstructionAttachment, item: MenuItem, popover: PopoverComponent) {
        this.selectedAttachment = attachment;
        if (item.command) {
            item.command({} as any);
        }
        popover.hide();
    }

    editAttachment(attachment: IConstructionAttachment) {
        this.isEditMode = true;
        this.selectedAttachment = attachment;
        this.attachmentForm = {
            name: attachment.name || '',
            description: attachment.description || '',
            expiryDate: attachment.expiryDate ? new Date(attachment.expiryDate) : null,
            file: null,
            attachment: attachment?.attachment ?? null
        };
        this.showAttachmentDialog = true;
    }

    closeAttachmentDialog() {
        this.showAttachmentDialog = false;
        this.attachmentForm = {
            name: '',
            description: '',
            expiryDate: null,
            file: null,
            attachment: null
        };
        this.selectedAttachment = null;
        this.isEditMode = false;
        this.formFile = null;
        if (this.fileDrop) {
            this.fileDrop.clearFiles();
        }
    }

    saveAttachment() {
        if (!this.attachmentForm.name.trim()) {
            this.toast.warn('Please enter attachment name');
            return;
        }
        this.saveAttachmentLoading = true;
        if (this.isEditMode && this.selectedAttachment) {
            const formData = new FormData();
            if (this.selectedAttachment._id) {
                formData.append('_id', this.selectedAttachment._id);
            }
            formData.append('name', this.attachmentForm.name);
            formData.append('description', this.attachmentForm.description);
            formData.append('expiryDate', this.attachmentForm.expiryDate ? this.attachmentForm.expiryDate.toISOString() : '');
            if (this.attachmentForm.file) {
                formData.append('file', this.attachmentForm.file as File);
            }
            formData.append('attachment', this.attachmentForm.attachment?._id as string ?? '');
            this.constructionAttachmentService.updateConstructionAttachment(formData).subscribe(res => {
                if (res && res.success) {
                    const newRecord = res.data;
                    this.attachments = this.attachments.map(attachment => attachment._id === newRecord._id ? { ...newRecord } : attachment);
                    this.paginatedAttachments = this.attachments.slice(this.first, this.first + this.rows);
                    this.toast.success('Attachment updated successfully');
                    this.closeAttachmentDialog();
                } else {
                    this.closeAttachmentDialog();
                }
                this.saveAttachmentLoading = false;
            })
        } else {
            const formData = new FormData();
            formData.append('name', this.attachmentForm.name);
            formData.append('description', this.attachmentForm.description);
            formData.append('expiryDate', this.attachmentForm.expiryDate ? this.attachmentForm.expiryDate.toISOString() : '');
            if (this.attachmentForm.file) {
                formData.append('file', this.attachmentForm.file as File);
            }
            this.constructionAttachmentService.updateConstructionAttachment(formData).subscribe(res => {
                if (res && res.success) {
                    const newRecord = res.data;
                    this.attachments = [newRecord, ...this.attachments];
                    this.totalRecords = this.attachments.length;
                    this.first = 0;
                    this.paginatedAttachments = this.attachments.slice(this.first, this.first + this.rows);
                    this.toast.success('Attachment created successfully');
                    this.closeAttachmentDialog();
                } else {
                    this.closeAttachmentDialog();
                }
                this.saveAttachmentLoading = false;
            })
        }
    }

    onFileUpload(event: File | File[]) {
        event = event as File[]
        this.formFile = event.length > 0 ? event[0] : null;
        this.attachmentForm.file = this.formFile;
    }

    onFileRemove(file: File) {
        this.formFile = null;
        this.attachmentForm.file = null;
    }

    getExpiryInfo(expiryDate: Date | null): { status: string; tooltip: string } | null {
        if (!expiryDate) return null;

        const today = new Date();
        const expiry = new Date(expiryDate);
        const diffTime = expiry.getTime() - today.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays < 0) {
            return {
                status: 'expired',
                tooltip: `Expired on ${expiry.toLocaleDateString()}`
            };
        } else if (diffDays <= 6) {
            return {
                status: 'expiring',
                tooltip: `Expires in ${diffDays} day${diffDays === 1 ? '' : 's'}`
            };
        }

        return null;
    }

}
